const ALL_MENU_PERMISSIONS = [
  'tableManage', // 餐厅管理
  'kitchenOrder', // 订单出餐
  'orderHistory', // 订单查询
  'kitchenServing', // 厨房出菜
  'chefScreen', // 厨师大屏
  'dishAssignment', // 菜品分配
  'servingHistory', // 出菜历史
  'kitchenDispatch', // 预定菜品
  'deliveryOrderFood', // 外卖菜品
  'pickCodeVerify', // 取餐码核销
  'buffetVerify', // 餐券核销
  'employeeCouponManage', // 员工餐券管理
  'foodManage', // 菜品管理
  'foodStockManage', // 菜品库存管理
  'orderReport', // 订单报表
] as const;

export const LEGACY_ROLE_MENU_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  kitchen_admin: ALL_MENU_PERMISSIONS.filter((menuName) => menuName !== 'employeeCouponManage'),
  kitchen_employee_coupon: ['employeeCouponManage'],
  kitchen_inventory: ['foodStockManage'],
};

export const KITCHEN_ROLE_MENU_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  admin: ALL_MENU_PERMISSIONS,
  ops: [
    'tableManage',
    'kitchenOrder',
    'orderHistory',
    'pickCodeVerify',
    'buffetVerify',
    'servingHistory',
    'foodManage',
    'foodStockManage',
    'orderReport',
  ],
  chef: [
    'kitchenServing',
    'chefScreen',
    'dishAssignment',
    'servingHistory',
    'kitchenDispatch',
    'deliveryOrderFood',
    'foodStockManage',
    'orderReport',
  ],
  inventory: ['foodStockManage'],
  employee_coupon: ['employeeCouponManage'],
};

export interface KitchenShopRoleModel {
  shopId: number;
  role: string;
}

const KITCHEN_ROLE_PATTERN = /^kitchen:(\d+):([a-z_]+)$/;

export function hasNewKitchenRoleFormat(roles: readonly string[]): boolean {
  return roles.some((role) => KITCHEN_ROLE_PATTERN.test(role));
}

export function parseKitchenShopRoles(roles: readonly string[]): KitchenShopRoleModel[] {
  return roles.flatMap((rawRole) => {
    const match = KITCHEN_ROLE_PATTERN.exec(rawRole);
    if (!match || !KITCHEN_ROLE_MENU_PERMISSIONS[match[2]]) return [];
    const shopId = Number(match[1]);
    return Number.isSafeInteger(shopId) && shopId >= 0 ? [{ shopId, role: match[2] }] : [];
  });
}

export function resolveAccessibleShopIds(roles: readonly string[]): number[] | null {
  if (!hasNewKitchenRoleFormat(roles)) {
    if (roles.includes('kitchen_admin') || roles.includes('kitchen_inventory')) return null;
    return [];
  }
  const shopRoles = parseKitchenShopRoles(roles).filter(({ role }) => role !== 'employee_coupon');
  if (shopRoles.some(({ shopId }) => shopId === 0)) return null;
  return Array.from(new Set(shopRoles.map(({ shopId }) => shopId)));
}

export function resolveDefaultShopId(roles: readonly string[]): number | undefined {
  return parseKitchenShopRoles(roles).find(
    ({ shopId, role }) => role !== 'employee_coupon' && shopId > 0,
  )?.shopId;
}

export function resolveMenuPermissions(
  roles: readonly string[],
  selectedShopId?: number,
): string[] {
  const permissions = new Set<string>();

  if (!hasNewKitchenRoleFormat(roles)) {
    roles.forEach((role) => {
      LEGACY_ROLE_MENU_PERMISSIONS[role]?.forEach((menuName) => permissions.add(menuName));
    });
  }

  parseKitchenShopRoles(roles)
    .filter(
      ({ shopId, role }) => role === 'employee_coupon' || shopId === 0 || shopId === selectedShopId,
    )
    .forEach(({ role }) => {
      KITCHEN_ROLE_MENU_PERMISSIONS[role].forEach((menuName) => permissions.add(menuName));
    });

  return Array.from(permissions);
}
